import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import { API_URL } from '..';

const AddWorkForceLayer = () => {
    const [formValues, setFormValues] = useState({
        fullName: '',
        address: '',
        postcode: '',
        phoneNumber: '',
        email: '',
        userImage: null
    });
    const [message, setMessage] = useState({
        status: false,
        reason: '',
        desc: ''
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isCameraActive, setIsCameraActive] = useState(false);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);

    // Handle camera initialization
    useEffect(() => {
        if (isCameraActive) {
            navigator.mediaDevices.getUserMedia({ video: true })
                .then(stream => {
                    if (videoRef.current) {
                        videoRef.current.srcObject = stream;
                    }
                })
                .catch(err => {
                    setMessage({ status: true, reason: 'error', desc: 'Failed to access camera: ' + err.message });
                    setIsCameraActive(false);
                });
        }

        // Cleanup camera on unmount or modal close
        return () => {
            if (videoRef.current && videoRef.current.srcObject) {
                const stream = videoRef.current.srcObject;
                const tracks = stream.getTracks();
                tracks.forEach(track => track.stop());
            }
        };
    }, [isCameraActive]);

    // Handle image selection from gallery
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setMessage({ status: true, reason: 'error', desc: 'Please select an image file' });
                return;
            }
            setFormValues(prev => ({ ...prev, userImage: file }));
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
            setShowModal(false);
        }
    };

    // Handle camera capture
    const handleCapture = () => {
        if (canvasRef.current && videoRef.current) {
            const context = canvasRef.current.getContext('2d');
            context.drawImage(videoRef.current, 0, 0, 300, 225);
            const imageData = canvasRef.current.toDataURL('image/png');
            setImagePreview(imageData);
            
            // Convert data URL to file
            fetch(imageData)
                .then(res => res.blob())
                .then(blob => {
                    const file = new File([blob], 'captured-image.png', { type: 'image/png' });
                    setFormValues(prev => ({ ...prev, userImage: file }));
                });
            
            // Stop camera
            setIsCameraActive(false);
            setShowModal(false);
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user) {
            setMessage({ status: true, reason: 'error', desc: 'User not authenticated. Please sign in.' });
            return;
        }

        // Validation
        if (!formValues.fullName) {
            setMessage({ status: true, reason: 'error', desc: 'Full name is required' });
            return;
        }
        if (!formValues.address) {
            setMessage({ status: true, reason: 'error', desc: 'Address is required' });
            return;
        }
        if (!formValues.phoneNumber) {
            setMessage({ status: true, reason: 'error', desc: 'Phone number is required' });
            return;
        }
        if (!formValues.userImage) {
            setMessage({ status: true, reason: 'error', desc: 'User image is required' });
            return;
        }
        if (formValues.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email)) {
            setMessage({ status: true, reason: 'error', desc: 'Invalid email format' });
            return;
        }

        try {
            const formData = new FormData();
            formData.append('fullName', formValues.fullName);
            formData.append('address', formValues.address);
            formData.append('postcode', formValues.postcode);
            formData.append('phoneNumber', formValues.phoneNumber);
            formData.append('email', formValues.email);
            formData.append('userImage', formValues.userImage);

            const response = await fetch(`${API_URL}/api/workforce/create`, {
                method: 'POST',
                body: formData
            });
            const data = await response.json();

            if (response.status === 201 || response.status === 200) {
                setMessage({ status: true, reason: 'success', desc: 'Workforce member added successfully' });
                setFormValues({
                    fullName: '',
                    address: '',
                    postcode: '',
                    phoneNumber: '',
                    email: '',
                    userImage: null
                });
                setImagePreview(null);
            } else {
                throw new Error(data.message || 'Failed to add workforce member');
            }
        } catch (error) {
            setMessage({ status: true, reason: 'error', desc: `Failed to add workforce member: ${error.message}` });
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body p-24">
                <style>
                    {`
                        .image-preview {
                            width: 150px;
                            height: 150px;
                            object-fit: cover;
                            border-radius: 8px;
                            border: 1px solid #ced4da;
                            cursor: pointer;
                        }
                        .image-preview.is-invalid {
                            border-color: #dc3545;
                        }
                        .modal-content {
                            border-radius: 8px;
                        }
                        .modal-icon {
                            font-size: 24px;
                            cursor: pointer;
                            padding: 10px;
                        }
                        .video-container {
                            width: 300px;
                            height: 225px;
                            border-radius: 8px;
                            overflow: hidden;
                        }
                    `}
                </style>
                <div className="row justify-content-center">
                    <div className="col-xxl-6 col-xl-8 col-lg-10">
                        <div className="card border">
                            <div className="card-body">
                                {/* Message Alert */}
                                {message.status && (
                                    <div className={`alert alert-${message.reason === 'error' ? 'danger' : 'success'} alert-dismissible fade show mb-20`} role="alert">
                                        {message.desc}
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() => setMessage({ ...message, status: false })}
                                            aria-label="Close"
                                        ></button>
                                    </div>
                                )}

                                {/* Workforce Form */}
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-20">
                                        <label
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            User Image <span className="text-danger-600">*</span>
                                        </label>
                                        <div
                                            className={`image-preview ${message.reason === 'error' && !formValues.userImage ? 'is-invalid' : ''}`}
                                            onClick={() => setShowModal(true)}
                                        >
                                            {imagePreview ? (
                                                <img src={imagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#f8f9fa' }}>
                                                    Click to add image
                                                </div>
                                            )}
                                        </div>
                                        {message.reason === 'error' && !formValues.userImage && (
                                            <div style={{ color: '#dc3545', fontSize: '0.875em' }}>User image is required</div>
                                        )}
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="fullName"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Full Name <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control radius-8"
                                            id="fullName"
                                            value={formValues.fullName}
                                            onChange={(e) => setFormValues(prev => ({ ...prev, fullName: e.target.value }))}
                                            placeholder="Full Name..."
                                        />
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="address"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Address <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control radius-8"
                                            id="address"
                                            value={formValues.address}
                                            onChange={(e) => setFormValues(prev => ({ ...prev, address: e.target.value }))}
                                            placeholder="Address..."
                                        />
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="postcode"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Postcode
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control radius-8"
                                            id="postcode"
                                            value={formValues.postcode}
                                            onChange={(e) => setFormValues(prev => ({ ...prev, postcode: e.target.value }))}
                                            placeholder="Postcode..."
                                        />
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="phoneNumber"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Phone Number <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="tel"
                                            className="form-control radius-8"
                                            id="phoneNumber"
                                            value={formValues.phoneNumber}
                                            onChange={(e) => setFormValues(prev => ({ ...prev, phoneNumber: e.target.value }))}
                                            placeholder="Phone Number..."
                                        />
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="email"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            className="form-control radius-8"
                                            id="email"
                                            value={formValues.email}
                                            onChange={(e) => setFormValues(prev => ({ ...prev, email: e.target.value }))}
                                            placeholder="Email..."
                                        />
                                    </div>
                                    <div className="d-flex align-items-center justify-content-center gap-3">
                                        <button
                                            type="submit"
                                            className="btn btn-primary border border-primary-400 text-md px-56 py-12 radius-8"
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </form>

                                {/* Modal for Image Selection */}
                                {showModal && (
                                    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                                        <div className="modal-dialog modal-dialog-centered">
                                            <div className="modal-content">
                                                <div className="modal-header">
                                                    <h6 className="modal-title">Select Image Source</h6>
                                                    <button
                                                        type="button"
                                                        className="btn-close"
                                                        onClick={() => {
                                                            setShowModal(false);
                                                            setIsCameraActive(false);
                                                        }}
                                                    ></button>
                                                </div>
                                                <div className="modal-body">
                                                    {!isCameraActive ? (
                                                        <div className="d-flex justify-content-center gap-5">
                                                            <div>
                                                            <Icon
                                                                style={{fontSize: 80}}
                                                                icon="material-symbols:camera"
                                                                onClick={() => setIsCameraActive(true)}
                                                                title="Use Camera"
                                                            />
                                                            <p>Use Camera</p>
                                                                </div>
                                                           <div>
                                                           <Icon
                                                                icon="material-symbols:photo-library"
                                                                style={{fontSize: 80}}
                                                                onClick={() => fileInputRef.current.click()}
                                                                title="Choose from Gallery"
                                                            />
                                                            <p>Use Gallery</p>
                                                            </div>
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                ref={fileInputRef}
                                                                style={{ display: 'none' }}
                                                                onChange={handleImageChange}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="text-center">
                                                            <div className="video-container">
                                                                <video ref={videoRef} autoPlay style={{ width: '100%', height: '100%' }}></video>
                                                            </div>
                                                            <button
                                                                className="btn btn-primary mt-3"
                                                                onClick={handleCapture}
                                                            >
                                                                Capture
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <canvas ref={canvasRef} width="300" height="225" style={{ display: 'none' }}></canvas>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddWorkForceLayer;